// ==================== MESSAGE ANALYTICS COMPONENT ====================

import { motion } from 'framer-motion';
import { BarChart3, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Select } from '../../../components/ui/Select';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { MessageAnalyticsData, DateRange } from '../../../services/customer-dashboard.service';
import { DATE_RANGE_OPTIONS, CHART_COLORS } from '../constants';

// ==================== TYPES ====================

interface MessageAnalyticsProps {
  data: MessageAnalyticsData | null;
  isLoading?: boolean;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}

// ==================== MINI BAR CHART ====================

const MiniBarChart = ({ 
  data, 
  maxHeight = 80,
}: { 
  data: Array<{ date: string; sent: number; delivered: number; read: number }>;
  maxHeight?: number;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-20 flex items-center justify-center text-(--color-muted-gray) text-sm">
        No data available
      </div>
    );
  }

  const maxValue = Math.max(...data.flatMap(d => [d.sent, d.delivered, d.read]), 1);

  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((day, index) => {
        const sentHeight = (day.sent / maxValue) * maxHeight;
        const deliveredHeight = (day.delivered / maxValue) * maxHeight;
        const readHeight = (day.read / maxValue) * maxHeight;
        
        return (
          <div key={day.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-(--color-midnight-navy) border border-(--color-border-gray) rounded-lg p-2 text-xs whitespace-nowrap shadow-lg">
                <p className="text-(--color-muted-gray) mb-1">{new Date(day.date).toLocaleDateString()}</p>
                <p className="text-(--color-signal-orange)">Sent: {day.sent}</p>
                <p className="text-green-400">Delivered: {day.delivered}</p>
                <p className="text-blue-400">Read: {day.read}</p>
              </div>
            </div>
            
            {/* Bars */}
            <div className="w-full flex gap-px">
              <div 
                className="flex-1 rounded-t transition-all duration-300"
                style={{ 
                  height: `${sentHeight}px`, 
                  backgroundColor: CHART_COLORS.sent,
                  minHeight: day.sent > 0 ? '2px' : '0',
                }}
              />
              <div 
                className="flex-1 rounded-t transition-all duration-300"
                style={{ 
                  height: `${deliveredHeight}px`, 
                  backgroundColor: CHART_COLORS.delivered,
                  minHeight: day.delivered > 0 ? '2px' : '0',
                }}
              />
              <div 
                className="flex-1 rounded-t transition-all duration-300"
                style={{ 
                  height: `${readHeight}px`, 
                  backgroundColor: CHART_COLORS.read,
                  minHeight: day.read > 0 ? '2px' : '0',
                }}
              />
            </div>
            
            {/* Date label for first/last/middle */}
            {(index === 0 || index === data.length - 1 || index === Math.floor(data.length / 2)) && (
              <span className="text-[10px] text-(--color-muted-gray) mt-1 truncate w-full text-center">
                {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==================== STAT PILL ====================

const StatPill = ({
  label,
  value,
  color,
  trend,
}: {
  label: string;
  value: number | string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
    <div className={cn('w-2 h-2 rounded-full', color)} />
    <div className="flex-1">
      <p className="text-xs text-(--color-muted-gray)">{label}</p>
      <p className="text-sm font-semibold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
    {trend && (
      <div>
        {trend === 'up' && <TrendingUp size={14} className="text-(--color-success)" />}
        {trend === 'down' && <TrendingDown size={14} className="text-(--color-error)" />}
        {trend === 'neutral' && <Minus size={14} className="text-(--color-muted-gray)" />}
      </div>
    )}
  </div>
);

// ==================== COMPONENT ====================

export const MessageAnalytics = ({ 
  data, 
  isLoading = false,
  dateRange,
  onDateRangeChange,
}: MessageAnalyticsProps) => {
  const dateRangeOptions = DATE_RANGE_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }));

  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card className="h-full">
          <CardHeader className="border-b-0 pb-1 mb-0">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">WhatsApp Analytics</CardTitle>
              <div className="w-28 h-7 bg-(--color-border-gray) rounded animate-pulse" />
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="h-20 bg-(--color-border-gray) rounded animate-pulse mb-3" />
            <div className="grid grid-cols-4 gap-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-(--color-border-gray) rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card className="h-full">
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-green-500" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp Analytics
            </CardTitle>
            <div className="w-32">
              <Select
                options={dateRangeOptions}
                value={dateRange}
                onChange={(value) => onDateRangeChange(value as DateRange)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {/* Chart */}
          <div className="mb-3">
            {data?.dailyStats && data.dailyStats.length > 0 ? (
              <MiniBarChart data={data.dailyStats} />
            ) : (
              <div className="h-20 flex items-center justify-center border border-dashed border-(--color-border-gray) rounded-lg">
                <div className="text-center">
                  <BarChart3 size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
                  <p className="text-xs text-(--color-muted-gray)">No message data for this period</p>
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mb-3 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS.sent }} />
              <span className="text-(--color-muted-gray)">Sent</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS.delivered }} />
              <span className="text-(--color-muted-gray)">Delivered</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: CHART_COLORS.read }} />
              <span className="text-(--color-muted-gray)">Read</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <StatPill 
              label="Total Sent" 
              value={data?.totals.sent || 0} 
              color="bg-(--color-signal-orange)" 
            />
            <StatPill 
              label="Delivered" 
              value={`${data?.totals.deliveryRate || 0}%`} 
              color="bg-green-400" 
              trend={data && data.totals.deliveryRate >= 90 ? 'up' : data && data.totals.deliveryRate < 70 ? 'down' : 'neutral'}
            />
            <StatPill 
              label="Read Rate" 
              value={`${data?.totals.readRate || 0}%`} 
              color="bg-blue-400" 
              trend={data && data.totals.readRate >= 50 ? 'up' : data && data.totals.readRate < 30 ? 'down' : 'neutral'}
            />
            <StatPill 
              label="Failed" 
              value={data?.totals.failed || 0} 
              color="bg-red-400" 
              trend={data && data.totals.failed > 0 ? 'down' : 'neutral'}
            />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default MessageAnalytics;
